// src/App.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Route, Routes, BrowserRouter, Outlet, useNavigate, Navigate } from 'react-router-dom';
import Header from './components/layout/Header';
import MobileMenu from './components/layout/MobileMenu';
import Footer from './components/layout/Footer';
import CheckoutPage from './pages/CheckoutPage';
import Booking from './pages/Booking';
import SearchPage from './pages/SearchPage';
import TeacherProfile from './pages/Profile';
import DoroosOnlineUserSelection from './pages/DoroosOnlineUserSelection';
import ContactFormPreview from './pages/ContactForm';
import HomePage from './pages/home/HomePage';
import AuthPage from './pages/AuthPage';
import Toast from './components/ui/Toast';
import { useUser } from './contexts/UserContext';
import MyCourses from './pages/MyCourses';
import OnlineClass from './pages/OnlineClass';
import PrivateLessonsManagement from './pages/PrivateLessonsManagement';
import TeacherMonthlyLessonsReport from './pages/TeacherMonthlyLessonsReport';
import ResetPassword from './pages/ResetPassword';
import Messages from './pages/Messages';
import AdminConfirmDocuments from './pages/AdminConfirmDocuments';
import AdminApproveTeachers from './pages/AdminApproveTeachers';
import AdminTeachersIncomeReport from './pages/AdminTeachersIncomeReport';
import AdminBankTransfers from './pages/AdminBankTransfers';
import AdminIncomeTaxReport from './pages/AdminIncomeTaxReport';
import AdminCourseImages from './pages/AdminCourseImages';
import AdminDashboardPage from './pages/AdminDashboard/AdminDashboardPage';
import SupplierPrograms from './pages/SupplierPrograms';
import SupplierDashboard from './pages/SupplierDashboard';
import SupplierMonthlyLessonsReport from './pages/SupplierMonthlyLessonsReport';
import SupplierTeacherMonthlyReport from './pages/SupplierTeacherMonthlyReport';
import SupplierTeachers from './pages/SupplierTeachers';
import TeamInvite from './pages/TeamInvite';
import SchoolDashboard from './pages/SchoolDashboard';
import SchoolPrograms from './pages/SchoolPrograms';
import AdminGpnBudgetPurchases from './pages/AdminGpnBudgetPurchases';
import RedeemCoupon from './pages/RedeemCoupon';
import MyWallet from './pages/MyWallet';


/** ✅ Redirect to Home ONLY when user logs out (prev logged in -> now logged out) */
const LogoutRedirectWatcher = () => {
  const navigate = useNavigate();
  const { isLogedin } = useUser();
  const prevLoggedInRef = useRef(!!isLogedin);

  useEffect(() => {
    const wasLoggedIn = prevLoggedInRef.current;
    const isNowLoggedIn = !!isLogedin;

    if (wasLoggedIn && !isNowLoggedIn) {
      navigate('/', { replace: true });
    }

    prevLoggedInRef.current = isNowLoggedIn;
  }, [isLogedin, navigate]);

  return null;
};

const AppLayout = ({ mobileMenuOpen, setMobileMenuOpen, burgerRef }) => {
  const { alert ,clearAlert } = useUser();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" dir="rtl">
      {alert && <Toast alert={alert} onClose={clearAlert} />}
      <LogoutRedirectWatcher />

      <Header
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        burgerRef={burgerRef}
      />

      {mobileMenuOpen && (
        <MobileMenu
          setMobileMenuOpen={setMobileMenuOpen}
          burgerRef={burgerRef}
        />
      )}

      <main className="flex-grow">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
};

/** ✅ /Admin should go directly to admin login (no user-type selection) */
const AdminAuthRoute = () => {
  const { role, isLogedin, setUserType } = useUser();

  // Force admin type immediately on entering /Admin
  useEffect(() => {
    setUserType('admin');
  }, [setUserType]);

  // If already logged in as admin -> go home (the index route renders the
  // admin dashboard for role==='admin', see below)
  if (isLogedin && role === 'admin') return <Navigate to="/" replace />;

  // If logged in as non-admin -> block
  if (isLogedin && role !== 'admin') return <Navigate to="/" replace />;

  // Not logged in -> show AuthPage directly in admin mode
  return <AuthPage />;
};

/** ✅ Admins land on the analytics dashboard instead of the public HomePage */
const IndexRoute = () => {
  const { role, user } = useUser();
  if (role === 'admin') return <AdminDashboardPage adminFirstName={user?.firstName} />;
  return <HomePage />;
};

const App = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const burgerRef = useRef(null);

  const { role, userType, isLogedin } = useUser();

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <AppLayout
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
              burgerRef={burgerRef}
            />
          }
        >
          <Route index element={<IndexRoute />} />

          {/* ✅ Admin route */}
          <Route path="Admin" element={<AdminAuthRoute />} />
          <Route path="AdminDashboard" element={role === 'admin' ? <AdminDashboardPage /> : <HomePage />} />
          <Route path="Messages" element={role === 'admin' ? <Messages/>: <HomePage />} />
          <Route path="TchsIncomeReport" element={role === 'admin' ? <AdminTeachersIncomeReport/>: <HomePage />} />
          <Route path="BankTransfersReport" element={role === 'admin' ? <AdminBankTransfers/>: <HomePage />} />
          <Route path="TchsTaxReport" element={role === 'admin' ? <AdminIncomeTaxReport/>: <HomePage />} />
          <Route path="ConfirmDocs" element={role === 'admin' ? <AdminConfirmDocuments/>: <HomePage />} />
          <Route path="ConfirmTchrs" element={role === 'admin' ? <AdminApproveTeachers/>: <HomePage />} />
          <Route path="SubPhotos" element={role === 'admin' ? <AdminCourseImages/>: <HomePage />} />
          <Route path="GpnBudgetPurchases" element={role === 'admin' ? <AdminGpnBudgetPurchases/>: <HomePage />} />

          {/* ✅ Supplier (learning center) routes */}
          <Route path="supplier/dashboard" element={role === 'supplier' ? <SupplierDashboard /> : <HomePage />} />
          <Route path="supplier/revenue" element={role === 'supplier' ? <SupplierMonthlyLessonsReport /> : <HomePage />} />
          <Route path="supplier/revenue/teacher" element={role === 'supplier' ? <SupplierTeacherMonthlyReport /> : <HomePage />} />
          <Route path="supplier/programs" element={role === 'supplier' ? <SupplierPrograms /> : <HomePage />} />
          <Route path="supplier/teachers" element={role === 'supplier' ? <SupplierTeachers /> : <HomePage />} />
          {/* Reuses the same MyCourses component the teacher/student
              routes use below — MyCourses.jsx already branches its
              display (ownership, empty-state copy, header text) on
              role, so a supplier hitting this route sees "קורסי מרכז
              הלמידה" instead of "הקורסים שלי" automatically. */}
          <Route path="supplier/courses" element={role === 'supplier' ? <MyCourses /> : <HomePage />} />

          {/* ✅ School (GEFEN) routes */}
          <Route path="school/dashboard" element={role === 'school' ? <SchoolDashboard /> : <HomePage />} />
          <Route path="school/programs" element={role === 'school' ? <SchoolPrograms /> : <HomePage />} />

          {/* ✅ Student GEFEN wallet / coupon redemption */}
          <Route path="RedeemCoupon" element={role === 'student' ? <RedeemCoupon /> : <HomePage />} />
          <Route path="MyWallet" element={role === 'student' ? <MyWallet /> : <HomePage />} />

          <Route path="Cart" element={role === 'student' ? <CheckoutPage /> : <HomePage />} />
          <Route path="Contact" element={<ContactFormPreview />} />
          <Route path="Profile" element={isLogedin ? <TeacherProfile /> : <HomePage />} />
          <Route path="login_page" element={role === 'default' ? <DoroosOnlineUserSelection /> : <HomePage />} />
          <Route path="Search" element={role === 'default' || role === 'student' ? <SearchPage /> : <HomePage />} />

          <Route
            path="AuthPage"
            element={
              !isLogedin
                ? (userType != null ? <AuthPage /> : <DoroosOnlineUserSelection />)
                : <HomePage />
            }
          />
         
          <Route path="MyCourses" element={<MyCourses />} />
          <Route path="/CourseMeetings/:courseId" element={<OnlineClass />} />
          <Route path="OnlineClass" element={<OnlineClass />} />
          <Route path="/bookPrivateLessons/:courseId" element={<Booking />} />
          <Route path="/PrivateLessonsRequests" element={<PrivateLessonsManagement />} />
          <Route path="/DoneLessons" element={<TeacherMonthlyLessonsReport />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/team-invite" element={<TeamInvite />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default App;